// Based on https://gist.github.com/gre/1650294
import { gsap } from 'gsap'
import { CustomEase } from 'gsap/all' // If you have the full GSAP package installed
process.client && gsap.registerPlugin(CustomEase)

// No easing, no acceleration
export function linear(t) {
	return t
}

// Slight acceleration from zero to full speed
export function easeInSine(t) {
	return -1 * Math.cos(t * (Math.PI / 2)) + 1
}

// Slight deceleration at the end
export function easeOutSine(t) {
	return Math.sin(t * (Math.PI / 2))
}

// Slight acceleration at beginning and slight deceleration at end
export function easeInOutSine(t) {
	return -0.5 * (Math.cos(Math.PI * t) - 1)
}

// Accelerating from zero velocity
export function easeInQuad(t) {
	return t * t
}

// Decelerating to zero velocity
export function easeOutQuad(t) {
	return t * (2 - t)
}

// Acceleration until halfway, then deceleration
export function easeInOutQuad(t) {
	return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
}

// Accelerating from zero velocity
export function easeInCubic(t) {
	return t * t * t
}

// Decelerating to zero velocity
export function easeOutCubic(t) {
	const t1 = t - 1
	return t1 * t1 * t1 + 1
}

// Acceleration until halfway, then deceleration
export function easeInOutCubic(t) {
	return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1
}

// Accelerating from zero velocity
export function easeInQuart(t) {
	return t * t * t * t
}

// Decelerating to zero velocity
export function easeOutQuart(t) {
	const t1 = t - 1
	return 1 - t1 * t1 * t1 * t1
}

// Acceleration until halfway, then deceleration
export function easeInOutQuart(t) {
	const t1 = t - 1
	return t < 0.5 ? 8 * t * t * t * t : 1 - 8 * t1 * t1 * t1 * t1
}

// Accelerating from zero velocity
export function easeInQuint(t) {
	return t * t * t * t * t
}

// Decelerating to zero velocity
export function easeOutQuint(t) {
	const t1 = t - 1
	return 1 + t1 * t1 * t1 * t1 * t1
}

// Acceleration until halfway, then deceleration
export function easeInOutQuint(t) {
	const t1 = t - 1
	return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * t1 * t1 * t1 * t1 * t1
}

// Accelerate exponentially until finish
export function easeInExpo(t) {
	if (t === 0) {
		return 0
	}

	return Math.pow(2, 10 * (t - 1))
}

// Initial exponential acceleration slowing to stop
export function easeOutExpo(t) {
	if (t === 1) {
		return 1
	}

	return -Math.pow(2, -10 * t) + 1
}

// Exponential acceleration and deceleration
export function easeInOutExpo(t) {
	if (t === 0 || t === 1) {
		return t
	}

	const scaledTime = t * 2
	const scaledTime1 = scaledTime - 1

	if (scaledTime < 1) {
		return 0.5 * Math.pow(2, 10 * scaledTime1)
	}

	return 0.5 * (-Math.pow(2, -10 * scaledTime1) + 2)
}

// Increasing velocity until stop
export function easeInCirc(t) {
	const scaledTime = t / 1
	return -1 * (Math.sqrt(1 - scaledTime * t) - 1)
}

// Start fast, decreasing velocity until stop
export function easeOutCirc(t) {
	const t1 = t - 1
	return Math.sqrt(1 - t1 * t1)
}

// Fast increase in velocity, fast decrease in velocity
export function easeInOutCirc(t) {
	const scaledTime = t * 2
	const scaledTime1 = scaledTime - 2

	if (scaledTime < 1) {
		return -0.5 * (Math.sqrt(1 - scaledTime * scaledTime) - 1)
	}

	return 0.5 * (Math.sqrt(1 - scaledTime1 * scaledTime1) + 1)
}

// Slow movement backwards then fast snap to finish
export function easeInBack(t, magnitude = 1.70158) {
	return t * t * ((magnitude + 1) * t - magnitude)
}

// Fast snap to backwards point then slow resolve to finish
export function easeOutBack(t, magnitude = 1.70158) {
	const scaledTime = t / 1 - 1

	return scaledTime * scaledTime * ((magnitude + 1) * scaledTime + magnitude) + 1
}

// Slow movement backwards, fast snap to past finish, slow resolve to finish
export function easeInOutBack(t, magnitude = 1.70158) {
	const scaledTime = t * 2
	const scaledTime2 = scaledTime - 2

	const s = magnitude * 1.525

	if (scaledTime < 1) {
		return 0.5 * scaledTime * scaledTime * ((s + 1) * scaledTime - s)
	}

	return 0.5 * (scaledTime2 * scaledTime2 * ((s + 1) * scaledTime2 + s) + 2)
}
// Bounces slowly then quickly to finish
export function easeInElastic(t, magnitude = 0.7) {
	if (t === 0 || t === 1) {
		return t
	}

	const scaledTime = t / 1
	const scaledTime1 = scaledTime - 1

	const p = 1 - magnitude
	const s = (p / (2 * Math.PI)) * Math.asin(1)

	return -(Math.pow(2, 10 * scaledTime1) * Math.sin(((scaledTime1 - s) * (2 * Math.PI)) / p))
}

// Fast acceleration, bounces to zero
export function easeOutElastic(t, magnitude = 0.7) {
	if (t === 0 || t === 1) {
		return t
	}

	const p = 1 - magnitude
	const scaledTime = t * 2

	const s = (p / (2 * Math.PI)) * Math.asin(1)
	return Math.pow(2, -10 * scaledTime) * Math.sin(((scaledTime - s) * (2 * Math.PI)) / p) + 1
}

// Slow start and end, two bounces sandwich a fast motion
export function easeInOutElastic(t, magnitude = 0.65) {
	if (t === 0 || t === 1) {
		return t
	}

	const p = 1 - magnitude
	const scaledTime = t * 2
	const scaledTime1 = scaledTime - 1

	const s = (p / (2 * Math.PI)) * Math.asin(1)

	if (scaledTime < 1) {
		return -0.5 * (Math.pow(2, 10 * scaledTime1) * Math.sin(((scaledTime1 - s) * (2 * Math.PI)) / p))
	}

	return Math.pow(2, -10 * scaledTime1) * Math.sin(((scaledTime1 - s) * (2 * Math.PI)) / p) * 0.5 + 1
}

// Bounce to completion
export function easeOutBounce(t) {
	const scaledTime = t / 1

	if (scaledTime < 1 / 2.75) {
		return 7.5625 * scaledTime * scaledTime
	} else if (scaledTime < 2 / 2.75) {
		const scaledTime2 = scaledTime - 1.5 / 2.75
		return 7.5625 * scaledTime2 * scaledTime2 + 0.75
	} else if (scaledTime < 2.5 / 2.75) {
		const scaledTime2 = scaledTime - 2.25 / 2.75
		return 7.5625 * scaledTime2 * scaledTime2 + 0.9375
	} else {
		const scaledTime2 = scaledTime - 2.625 / 2.75
		return 7.5625 * scaledTime2 * scaledTime2 + 0.984375
	}
}

// Bounce increasing in velocity until completion
export function easeInBounce(t) {
	return 1 - easeOutBounce(1 - t)
}

// Bounce in and bounce out
export function easeInOutBounce(t) {
	if (t < 0.5) {
		return easeInBounce(t * 2) * 0.5
	}

	return easeOutBounce(t * 2 - 1) * 0.5 + 0.5
}

export function getGsapCustomEasing(easeType) {
	// Create an object to store all easing functions
	const easeMap = {
		'in-quad': [0.55, 0.085, 0.68, 0.53],
		'in-cubic': [0.55, 0.055, 0.675, 0.19],
		'in-quart': [0.895, 0.03, 0.685, 0.22],
		'in-quint': [0.755, 0.05, 0.855, 0.06],
		'in-sine': [0.47, 0, 0.745, 0.715],
		'in-expo': [0.95, 0.05, 0.795, 0.035],
		'in-circ': [0.6, 0.04, 0.98, 0.335],
		'in-back': [0.6, -0.28, 0.735, 0.045],
		'out-quad': [0.25, 0.46, 0.45, 0.94],
		'out-cubic': [0.215, 0.61, 0.355, 1],
		'out-quart': [0.165, 0.84, 0.44, 1],
		'out-quint': [0.23, 1, 0.32, 1],
		'out-sine': [0.39, 0.575, 0.565, 1],
		'out-expo': [0.19, 1, 0.22, 1],
		'out-circ': [0.075, 0.82, 0.165, 1],
		'out-back': [0.175, 0.885, 0.32, 1.275],
		'in-out-quad': [0.455, 0.03, 0.515, 0.955],
		'in-out-cubic': [0.645, 0.045, 0.355, 1],
		'in-out-quart': [0.77, 0, 0.175, 1],
		'in-out-quint': [0.86, 0, 0.07, 1],
		'in-out-sine': [0.445, 0.05, 0.55, 0.95],
		'in-out-expo': [1, 0, 0, 1],
		'in-out-circ': [0.785, 0.135, 0.15, 0.86],
		'in-out-back': [0.68, -0.55, 0.265, 1.55],
	}

	const easingValues = easeMap[easeType]

	if (!easingValues) {
		throw new Error(`Easing type "${easeType}" not found.`)
	}

	return CustomEase.create('custom', `${easingValues.join(', ')}`)
	// return `cubic-bezier(${easingValues.join(', ')})`
}
